import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowRight,
  CheckCircle,
  Heart,
  Users,
  Target,
  FileText,
  Calendar,
  Download,
  ClipboardCheck,
  Lightbulb,
  MessageSquare,
  Clock,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import useFormValidation from "../../hooks/useFormValidation";
import SEO from "../../components/SEO";
import StructuredData from "../../components/StructuredData";
import AnimateOnScroll from "../../components/AnimateOnScroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROGRAM_GUIDE_URL = "https://customer-assets.emergentagent.com/job_client-connect-55/artifacts/ml2n4bjm_CareRevive%20Initiative%E2%84%A2%20Program%20Overview%20%28Detailed%20PDF%20Version%29.pdf";

const CareRevive = () => {
  const [searchParams] = useSearchParams();
  const [showApplication, setShowApplication] = useState(false);
  const [showDownloadGate, setShowDownloadGate] = useState(false);
  const [downloadEmail, setDownloadEmail] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [formStep, setFormStep] = useState(1);
  
  // Add-on selections
  const [selectedAddons, setSelectedAddons] = useState({
    plus: false,
    leadership_lab: false,
    ongoing: false,
  });
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState("");
  const [discountLoading, setDiscountLoading] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState("");
  
  // Referral code state
  const [referralCode, setReferralCode] = useState(searchParams.get("ref") || "");
  const [referralValidating, setReferralValidating] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  const [referralPartnerName, setReferralPartnerName] = useState("");

  const BASE_PRICE = 750;
  
  const [formData, setFormData] = useState({
    // Practice Basics
    applicant_name: "",
    applicant_email: "",
    contact_phone: "",
    company_name: "",
    practice_location: "",
    role_in_organization: "",
    // Practice Size & Structure
    num_providers: "",
    company_size: "",
    services_offered: "",
    // Current Challenges
    challenges: "",
    team_morale: "",
    // Goals & Motivation
    motivation: "",
    goals: "",
    // Workflow & Operations
    workflows_to_improve: "",
    previous_solutions: "",
    // Team & Culture
    team_culture: "",
    recent_changes: "",
    // Engagement & Readiness
    leadership_support: "",
    can_dedicate_time: "",
    // Contact & Next Steps
    contact_person: "",
    anything_else: "",
    // Legacy fields for API compatibility
    industry: "healthcare",
    timeline: "4-6 weeks",
    budget: "",
  });
  
  // Dynamic pricing based on staff size
  const getAddonPrices = (staffSize) => {
    const pricingTiers = {
      "1-5": { plus: 1500, leadership_lab: 750, ongoing: 1500 },
      "6-10": { plus: 1750, leadership_lab: 875, ongoing: 1750 },
      "11-25": { plus: 2000, leadership_lab: 1000, ongoing: 2000 },
      "26-50": { plus: 2500, leadership_lab: 1125, ongoing: 2500 },
      "50+": { plus: 3000, leadership_lab: 1250, ongoing: 3000 },
    };
    return pricingTiers[staffSize] || pricingTiers["11-25"]; // Default to mid-tier
  };

  const currentAddonPrices = getAddonPrices(formData.company_size);

  const calculateSubtotal = () => {
    let total = BASE_PRICE;
    Object.entries(selectedAddons).forEach(([key, selected]) => {
      if (selected) total += currentAddonPrices[key];
    });
    return total;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    if (!appliedDiscount) return subtotal;
    
    if (appliedDiscount.discount_type === "percentage") {
      return Math.max(0, subtotal - (subtotal * appliedDiscount.discount_value / 100));
    } else {
      return Math.max(0, subtotal - appliedDiscount.discount_value);
    }
  };

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) return;
    
    setDiscountLoading(true);
    setDiscountError("");
    
    try {
      const params = new URLSearchParams({
        code: discountCode.trim(),
        program_type: "carerevive",
        amount: calculateSubtotal().toString()
      });
      
      const response = await fetch(`${API}/programs/validate-discount?${params.toString()}`, {
        method: "POST"
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Invalid discount code");
      }
      
      setAppliedDiscount(data);
      toast.success(`Discount code "${data.code}" applied!`);
    } catch (error) {
      setDiscountError(error.message);
      setAppliedDiscount(null);
    } finally {
      setDiscountLoading(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode("");
    setDiscountError("");
  };

  // Validate referral code
  const validateReferralCode = async (code) => {
    if (!code || code.trim().length === 0) {
      setReferralValid(null);
      setReferralPartnerName("");
      return;
    }
    
    setReferralValidating(true);
    try {
      const response = await fetch(`${API}/referrals/validate-code/${code.trim()}`);
      const data = await response.json();
      
      setReferralValid(data.valid);
      setReferralPartnerName(data.valid ? data.partner_name : "");
      
      if (data.valid) {
        toast.success(`Referral code applied! Referred by ${data.partner_name}`);
      }
    } catch (error) {
      setReferralValid(false);
      setReferralPartnerName("");
    } finally {
      setReferralValidating(false);
    }
  };

  // Validate referral code from URL on mount
  useEffect(() => {
    const refCode = searchParams.get("ref");
    if (refCode) {
      validateReferralCode(refCode);
    }
  }, [searchParams]);

  const toggleAddon = (key) => {
    setSelectedAddons(prev => ({ ...prev, [key]: !prev[key] }))
  };

  // Validation configuration
  const validationConfig = {
    requiredFieldsByStep: {
      // Step 1: Practice Basics
      1: ["applicant_name", "applicant_email", "contact_phone", "company_name", "practice_location", "role_in_organization"],
      // Step 2: Practice Size & Structure + Current Challenges (services_offered and challenges are both in step 2 UI)
      2: ["num_providers", "company_size", "services_offered", "challenges"],
      // Step 3: Goals & Motivation (team_morale is optional - no * in UI)
      3: ["motivation", "goals"],
      // Step 4: Team & Culture + Engagement & Readiness
      4: ["leadership_support", "can_dedicate_time"],
    },
    fieldLabels: {
      applicant_name: "Your Name",
      applicant_email: "Email Address",
      contact_phone: "Phone Number",
      company_name: "Practice Name",
      practice_location: "Practice Location",
      role_in_organization: "Your Role",
      num_providers: "Number of Providers",
      company_size: "Staff Size",
      services_offered: "Services Offered",
      challenges: "Top Challenges",
      motivation: "Motivation for CareRevive",
      goals: "Goals",
      leadership_support: "Leadership Support",
      can_dedicate_time: "Time Commitment",
    },
    emailFields: ["applicant_email"],
  };

  const { 
    validationErrors, 
    validateStep, 
    validateAllSteps, 
    clearFieldError 
  } = useFormValidation(formData, validationConfig);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearFieldError(field);
  };

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const paymentParam = searchParams.get("payment");

    if (sessionId && paymentParam === "success") {
      pollPaymentStatus(sessionId);
    } else if (paymentParam === "cancelled") {
      toast.error("Payment was cancelled");
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      setPaymentStatus("timeout");
      return;
    }

    try {
      const response = await fetch(`${API}/programs/payment-status/${sessionId}`);
      const data = await response.json();

      if (data.payment_status === "paid") {
        setPaymentStatus("success");
        toast.success("Payment successful! Welcome to CareRevive!");
      } else if (data.status === "expired") {
        setPaymentStatus("expired");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all steps before submission
    if (!validateAllSteps()) {
      toast.error("Please fill in all required fields before submitting");
      return;
    }
    
    setLoading(true);

    try {
      // Build comprehensive application data
      const applicationData = {
        program_type: "carerevive",
        applicant_name: formData.applicant_name,
        applicant_email: formData.applicant_email,
        company_name: formData.company_name,
        company_size: formData.company_size,
        industry: formData.industry,
        challenges: `Top Challenges: ${formData.challenges}\n\nTeam Morale: ${formData.team_morale}`,
        goals: `Motivation: ${formData.motivation}\n\nGoals: ${formData.goals}`,
        timeline: formData.timeline,
        budget: formData.budget,
        contact_phone: formData.contact_phone,
        // Additional fields stored in challenges/goals for now
      };

      const params = new URLSearchParams(applicationData);

      const response = await fetch(`${API}/programs/apply?${params.toString()}`, {
        method: "POST",
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.detail || "Application failed");

      setApplicationId(data.application_id);
      toast.success("Application submitted!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!applicationId) return;
    setLoading(true);

    try {
      // Build addons query string
      const addonKeys = Object.entries(selectedAddons)
        .filter(([_, selected]) => selected)
        .map(([key]) => key)
        .join(",");
      
      const params = new URLSearchParams();
      if (addonKeys) params.append("addons", addonKeys);
      if (appliedDiscount) params.append("discount_code", appliedDiscount.code);
      if (referralCode && referralValid) params.append("referral_code", referralCode);
      
      const queryString = params.toString();
      const url = `${API}/programs/checkout/${applicationId}${queryString ? `?${queryString}` : ""}`;
      
      const response = await fetch(url, {
        method: "POST",
        headers: { origin: window.location.origin },
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.detail || "Checkout failed");

      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message);
      setLoading(false);
    }
  };

  const handleDownloadGuide = async (e) => {
    e.preventDefault();
    if (!downloadEmail) {
      toast.error("Please enter your email");
      return;
    }
    
    setDownloadLoading(true);
    
    try {
      // Save email to contacts
      const params = new URLSearchParams({
        name: "Program Guide Download",
        email: downloadEmail,
        message: "Downloaded CareRevive Program Guide",
        service_interest: "carerevive",
      });
      
      await fetch(`${API}/contact?${params.toString()}`, { method: "POST" });
      
      // Trigger download
      window.open(PROGRAM_GUIDE_URL, "_blank");
      setShowDownloadGate(false);
      setDownloadEmail("");
      toast.success("Download started! Check your downloads folder.");
    } catch (error) {
      // Still allow download even if contact save fails
      window.open(PROGRAM_GUIDE_URL, "_blank");
      setShowDownloadGate(false);
    } finally {
      setDownloadLoading(false);
    }
  };

  const careFramework = [
    {
      letter: "C",
      title: "Check the Pulse",
      description: "Anonymous team survey and leadership conversation to assess burnout, engagement, and team culture.",
    },
    {
      letter: "A",
      title: "Align the Work",
      description: "Deep dive into critical workflows and create a Role Clarity Map to reduce confusion and overlap.",
    },
    {
      letter: "R",
      title: "Revive the Team",
      description: "60-90 minute facilitated team session to share findings, celebrate strengths, and introduce new habits.",
    },
    {
      letter: "E",
      title: "Embed the Habits",
      description: "Custom CareRevive Team Playbook with actionable steps and a 30-90 day practical plan.",
    },
  ];

  const whatsIncluded = [
    { icon: ClipboardCheck, title: "Burnout & Engagement Survey", description: "Anonymous team survey to uncover real issues" },
    { icon: Users, title: "1:1 Leadership Consult", description: "Private session with practice leadership" },
    { icon: Target, title: "Workflow Review + Role Clarity Map", description: "Clear roles and reduced confusion" },
    { icon: MessageSquare, title: "Team Session (Virtual)", description: "60-90 minute facilitated group session" },
    { icon: FileText, title: "CareRevive Team Playbook", description: "Custom PDF with actionable steps" },
    { icon: Clock, title: "30-Minute Follow-Up", description: "Progress check and plan adjustments" },
  ];

  const addOns = [
    {
      name: "CareRevive Plus",
      description: "Implementation support for plan rollout",
      price: "$1,500–$3,000",
    },
    {
      name: "Leadership Lab",
      description: "Leadership training for managers",
      price: "$750–$1,250",
    },
    {
      name: "Ongoing Partnership",
      description: "Monthly support and coaching",
      price: "$1,500–$3,000/month",
    },
  ];

  const faqs = [
    {
      question: "How long does CareRevive take?",
      answer: "The program runs 4-6 weeks, with flexible scheduling to fit your practice's needs.",
    },
    {
      question: "Is it virtual or onsite?",
      answer: "The standard package is fully virtual. Onsite options are available with a custom quote.",
    },
    {
      question: "What's required from my team?",
      answer: "Participation in the anonymous survey, attending the team session, and providing feedback. We keep time requirements minimal.",
    },
    {
      question: "Is this a one-size-fits-all program?",
      answer: "No—each engagement is tailored to your unique needs, team size, and challenges.",
    },
    {
      question: "What if I need more support after the program?",
      answer: "We offer optional add-ons including implementation support, leadership training, and ongoing monthly partnerships.",
    },
  ];

  const nextStep = () => {
    if (validateStep(formStep)) {
      setFormStep(prev => Math.min(prev + 1, 4));
    } else {
      toast.error("Please fill in all required fields before continuing");
    }
  };
  
  const prevStep = () => setFormStep(prev => Math.max(prev - 1, 1));

  if (paymentStatus === "success") {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-rnb-light to-white">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-rnb-teal/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-rnb-teal" />
          </div>
          <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-4">
            Welcome to CareRevive!
          </h2>
          <p className="text-rnb-gray mb-6">
            Your enrollment is complete. Our team will contact you within 24 hours to schedule 
            your kickoff session.
          </p>
          <Link to="/portal">
            <Button className="btn-hover-lift">
              Go to Client Portal
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Form step rendering
  const renderFormStep = () => {
    switch (formStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2">
              Practice Basics
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Your Name *</Label>
                <Input
                  value={formData.applicant_name}
                  onChange={(e) => handleChange("applicant_name", e.target.value)}
                  placeholder="Full name"
                  required
                  data-testid="cr-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.applicant_email}
                  onChange={(e) => handleChange("applicant_email", e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="cr-email"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone Number *</Label>
                <Input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => handleChange("contact_phone", e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                  data-testid="cr-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Practice Name *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => handleChange("company_name", e.target.value)}
                  placeholder="Your practice name"
                  required
                  data-testid="cr-practice"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Practice Location (City, State) *</Label>
                <Input
                  value={formData.practice_location}
                  onChange={(e) => handleChange("practice_location", e.target.value)}
                  placeholder="Atlanta, GA"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Your Role in the Organization *</Label>
                <Select
                  value={formData.role_in_organization}
                  onValueChange={(value) => handleChange("role_in_organization", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your role..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner/Founder</SelectItem>
                    <SelectItem value="practice-manager">Practice Manager</SelectItem>
                    <SelectItem value="office-manager">Office Manager</SelectItem>
                    <SelectItem value="clinical-director">Clinical Director</SelectItem>
                    <SelectItem value="hr">HR/People Operations</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2">
              Practice Size & Structure
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>How many providers work at your practice? *</Label>
                <Select
                  value={formData.num_providers}
                  onValueChange={(value) => handleChange("num_providers", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 provider</SelectItem>
                    <SelectItem value="2-5">2-5 providers</SelectItem>
                    <SelectItem value="6-10">6-10 providers</SelectItem>
                    <SelectItem value="11-20">11-20 providers</SelectItem>
                    <SelectItem value="20+">20+ providers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Total staff members (clinical + admin)? *</Label>
                <Select
                  value={formData.company_size}
                  onValueChange={(value) => handleChange("company_size", value)}
                >
                  <SelectTrigger data-testid="cr-size">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-5">1-5 staff</SelectItem>
                    <SelectItem value="6-10">6-10 staff</SelectItem>
                    <SelectItem value="11-25">11-25 staff</SelectItem>
                    <SelectItem value="26-50">26-50 staff</SelectItem>
                    <SelectItem value="50+">50+ staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>What types of services does your practice offer? *</Label>
              <Textarea
                value={formData.services_offered}
                onChange={(e) => handleChange("services_offered", e.target.value)}
                placeholder="e.g., Primary care, behavioral health, physical therapy, home health..."
                rows={3}
                required
              />
            </div>
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2 mt-6">
              Current Challenges
            </h3>
            <div className="space-y-2">
              <Label>What are the top 2-3 challenges your team is facing right now? *</Label>
              <Textarea
                value={formData.challenges}
                onChange={(e) => handleChange("challenges", e.target.value)}
                placeholder="e.g., burnout, workflow efficiency, communication, compliance, turnover..."
                rows={3}
                required
                data-testid="cr-challenges"
              />
            </div>
            <div className="space-y-2">
              <Label>How would you describe your team&apos;s current morale and engagement?</Label>
              <Select
                value={formData.team_morale}
                onValueChange={(value) => handleChange("team_morale", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excellent">Excellent - Team is thriving</SelectItem>
                  <SelectItem value="good">Good - Generally positive</SelectItem>
                  <SelectItem value="fair">Fair - Some concerns</SelectItem>
                  <SelectItem value="struggling">Struggling - Significant issues</SelectItem>
                  <SelectItem value="critical">Critical - Urgent intervention needed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2">
              Goals & Motivation
            </h3>
            <div className="space-y-2">
              <Label>What motivated you to seek support through CareRevive? *</Label>
              <Textarea
                value={formData.motivation}
                onChange={(e) => handleChange("motivation", e.target.value)}
                placeholder="What brought you here today?"
                rows={3}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>What outcomes or improvements are you hoping to achieve? *</Label>
              <Textarea
                value={formData.goals}
                onChange={(e) => handleChange("goals", e.target.value)}
                placeholder="What does success look like for your team?"
                rows={3}
                required
              />
            </div>
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2 mt-6">
              Workflow & Operations
            </h3>
            <div className="space-y-2">
              <Label>Are there specific workflows or processes you&apos;d like to improve?</Label>
              <Textarea
                value={formData.workflows_to_improve}
                onChange={(e) => handleChange("workflows_to_improve", e.target.value)}
                placeholder="Describe any specific processes that need attention..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Have you tried any previous solutions or initiatives? If so, what were they?</Label>
              <Textarea
                value={formData.previous_solutions}
                onChange={(e) => handleChange("previous_solutions", e.target.value)}
                placeholder="Tell us about any past efforts to address these issues..."
                rows={3}
              />
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2">
              Team & Culture
            </h3>
            <div className="space-y-2">
              <Label>How would you describe your team culture in a sentence or two?</Label>
              <Textarea
                value={formData.team_culture}
                onChange={(e) => handleChange("team_culture", e.target.value)}
                placeholder="e.g., We're a tight-knit team that works hard but sometimes struggles with communication..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Are there recent changes or events that have impacted your team?</Label>
              <Textarea
                value={formData.recent_changes}
                onChange={(e) => handleChange("recent_changes", e.target.value)}
                placeholder="e.g., growth, turnover, leadership changes, expansion..."
                rows={2}
              />
            </div>
            <h3 className="font-heading text-lg font-semibold text-rnb-navy border-b pb-2 mt-6">
              Engagement & Readiness
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Is your leadership team supportive of this initiative? *</Label>
                <Select
                  value={formData.leadership_support}
                  onValueChange={(value) => handleChange("leadership_support", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fully">Fully supportive</SelectItem>
                    <SelectItem value="mostly">Mostly supportive</SelectItem>
                    <SelectItem value="somewhat">Somewhat supportive</SelectItem>
                    <SelectItem value="undecided">Still deciding</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Can you dedicate time for sessions over 4-6 weeks? *</Label>
                <Select
                  value={formData.can_dedicate_time}
                  onValueChange={(value) => handleChange("can_dedicate_time", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes, we can commit</SelectItem>
                    <SelectItem value="limited">Limited availability</SelectItem>
                    <SelectItem value="flexible">Need flexible scheduling</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Who should we contact for next steps and scheduling?</Label>
              <Input
                value={formData.contact_person}
                onChange={(e) => handleChange("contact_person", e.target.value)}
                placeholder="Name and preferred contact method"
              />
            </div>
            <div className="space-y-2">
              <Label>Is there anything else you&apos;d like us to know?</Label>
              <Textarea
                value={formData.anything_else}
                onChange={(e) => handleChange("anything_else", e.target.value)}
                placeholder="Any additional information about your practice or team..."
                rows={3}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <SEO 
        title="CareRevive — Fix Staff Burnout & Rebuild Team Culture"
        description="Struggling with staff burnout, low morale, or high turnover? CareRevive is a proven 4-step program to rebuild your healthcare team's culture and engagement. From $750."
        ogImage="https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/87gscl65_How%20PEOs%20Streamline%20HR%20and%20Payroll%20for%20Small%20Medical%20and%20Dental%20Practices.webp"
        keywords="healthcare staff burnout solutions, how to improve team morale in clinic, medical staff retention, healthcare workplace culture, reduce turnover in medical practice, team engagement healthcare"
      />
      <StructuredData data={[{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
          { "@type": "ListItem", "position": 2, "name": "Programs", "item": "https://www.rnbconsultinggroup.com/services" },
          { "@type": "ListItem", "position": 3, "name": "CareRevive", "item": "https://www.rnbconsultinggroup.com/programs/carerevive" }
        ]
      }]} />
      
      {/* Hero Section */}
      <section className="relative py-28 bg-gradient-to-br from-rnb-light to-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-blue/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimateOnScroll direction="left">
              <div>
                <div className="inline-flex items-center px-4 py-2 bg-rnb-blue/10 rounded-full mb-6">
                  <Heart className="w-4 h-4 text-rnb-blue mr-2" />
                  <span className="text-sm font-medium text-rnb-navy">Employee Wellness Program</span>
                </div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-rnb-navy mb-6">
                CareRevive Initiative™
              </h1>

              <p className="text-xl text-rnb-navy font-medium mb-4">
                Reduce Burnout. Strengthen Leadership. Improve Team Culture.
              </p>

              <p className="text-lg text-rnb-gray mb-8">
                A 4-6 week employee wellness and engagement program specifically designed for 
                small healthcare practices. Get practical solutions without overwhelming your staff.
              </p>

              <div className="flex items-end gap-4 mb-8">
                <div>
                  <p className="text-sm text-rnb-gray mb-1">Starter Package</p>
                  <p className="font-mono-numbers text-4xl font-bold text-rnb-navy">$750</p>
                </div>
                <p className="text-sm text-rnb-gray pb-1">One-time • 4-6 weeks</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="btn-hover-lift"
                  onClick={() => setShowApplication(true)}
                  data-testid="carerevive-apply-btn"
                >
                  Get Started – $750
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => setShowDownloadGate(true)}
                  data-testid="download-guide-btn"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Program Guide
                </Button>
              </div>
            </div>
            </AnimateOnScroll>

            <AnimateOnScroll direction="right">
              <div className="relative hidden lg:block">
                <img
                  src="https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/87gscl65_How%20PEOs%20Streamline%20HR%20and%20Payroll%20for%20Small%20Medical%20and%20Dental%20Practices.webp"
                  alt="Healthcare provider with tablet speaking warmly with a patient"
                  className="img-warm w-full"
                />
              </div>
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-8 text-center">
            Is CareRevive Right for You?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-rnb-blue/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-rnb-blue" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-2">Healthcare Practices</h3>
                <p className="text-sm text-rnb-gray">1-20 providers and 5-50 staff</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-rnb-blue/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Lightbulb className="w-6 h-6 text-rnb-blue" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-2">Leaders</h3>
                <p className="text-sm text-rnb-gray">Who care about their teams but feel overwhelmed or stuck</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-rnb-blue/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-6 h-6 text-rnb-blue" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-2">Teams</h3>
                <p className="text-sm text-rnb-gray">Struggling with burnout, turnover, or unclear roles</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CARE Framework */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              The CARE Framework
            </h2>
            <p className="text-rnb-gray">How CareRevive Works – Step by Step</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {careFramework.map((step, index) => (
              <Card key={step.title || step.name || i} className="border-0 shadow-sm card-hover relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-rnb-blue" />
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-rnb-blue rounded-xl flex items-center justify-center mb-4">
                    <span className="font-heading text-2xl font-bold text-white">{step.letter}</span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-rnb-gray">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              What&apos;s Included in the Starter Package
            </h2>
            <p className="text-rnb-gray">Everything you need for $750</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whatsIncluded.map((item, index) => (
              <div key={item.title || item.name || item.label || index} className="flex items-start gap-4 p-6 bg-slate-50 rounded-xl">
                <div className="w-12 h-12 bg-rnb-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-rnb-blue" />
                </div>
                <div>
                  <h3 className="font-medium text-rnb-navy mb-1">{item.title}</h3>
                  <p className="text-sm text-rnb-gray">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expected Outcomes */}
      <section className="py-16 bg-rnb-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-8 text-center">
            Expected Outcomes
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Clear understanding of team struggles and strengths",
              "Reduced burnout and confusion",
              "Improved workflow and morale",
              "Practical, sustainable changes in 30-90 days",
            ].map((item, index) => (
              <div key={item.title || item.name || item.label || index} className="flex items-center gap-3 p-4 bg-white/10 rounded-lg">
                <CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" />
                <span className="text-white">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-Ons */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-4">
              Optional Add-Ons
            </h2>
            <p className="text-rnb-gray">Take your transformation further</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {addOns.map((addon, index) => (
              <Card key={addon.name || addon.title || i} className="border-2 border-dashed border-slate-200">
                <CardContent className="p-6 text-center">
                  <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-2">
                    {addon.name}
                  </h3>
                  <p className="text-sm text-rnb-gray mb-4">{addon.description}</p>
                  <p className="font-mono-numbers text-xl font-bold text-rnb-blue">{addon.price}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-center text-sm text-rnb-gray mt-6">
            Custom quotes available for larger teams or additional workflows
          </p>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-16 bg-rnb-light">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Card className="border-2 border-rnb-blue">
            <CardContent className="p-8">
              <h3 className="font-heading text-2xl font-bold text-rnb-navy mb-2">
                CareRevive Starter Package
              </h3>
              <p className="font-mono-numbers text-5xl font-bold text-rnb-navy mb-4">$750</p>
              <p className="text-rnb-gray mb-6">One-time fee • 4-6 week program • Fully virtual</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="btn-hover-lift"
                  onClick={() => setShowApplication(true)}
                >
                  Get Started Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Link to="/contact">
                  <Button variant="outline" size="lg">
                    <Calendar className="mr-2 h-4 w-4" />
                    Schedule Free Consultation
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-rnb-navy mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={faq.question || faq.title || i} value={`faq-${index}`} className="border rounded-lg px-4">
                <AccordionTrigger className="text-left font-medium text-rnb-navy hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-rnb-gray">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Download Guide Modal */}
      <Dialog open={showDownloadGate} onOpenChange={setShowDownloadGate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Download CareRevive Program Guide</DialogTitle>
            <DialogDescription>
              Enter your email to receive the detailed program guide PDF.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDownloadGuide} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="download-email">Email Address *</Label>
              <Input
                id="download-email"
                type="email"
                value={downloadEmail}
                onChange={(e) => setDownloadEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="download-email"
              />
            </div>
            <Button type="submit" className="w-full btn-hover-lift" disabled={downloadLoading}>
              {downloadLoading ? "Processing..." : "Download Program Guide"}
              <Download className="ml-2 h-4 w-4" />
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Application Modal */}
      {showApplication && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardContent className="p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-rnb-navy">
                    {applicationId ? "Complete Your Enrollment" : "CareRevive™ Application"}
                  </h2>
                  <p className="text-rnb-gray text-sm mt-1">
                    {applicationId
                      ? "Select any optional add-ons and complete payment below."
                      : `Step ${formStep} of 4`}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowApplication(false);
                    setApplicationId(null);
                    setFormStep(1);
                  }}
                  className="text-rnb-gray hover:text-rnb-navy text-xl"
                >
                  ✕
                </button>
              </div>

              {applicationId ? (
                <div className="py-6">
                  <div className="text-center mb-6">
                    <CheckCircle className="w-16 h-16 text-rnb-teal mx-auto mb-4" />
                    <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-2">
                      Application Submitted!
                    </h3>
                    <p className="text-rnb-gray">
                      Select any optional add-ons and complete payment below.
                    </p>
                  </div>

                  {/* Add-on Options */}
                  <div className="space-y-3 mb-6">
                    <p className="text-sm font-medium text-rnb-navy">Optional Add-ons:</p>
                    
                    {/* CareRevive Plus */}
                    <div 
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${selectedAddons.plus ? 'border-rnb-blue bg-rnb-light' : 'bg-slate-50 hover:border-slate-300'}`}
                      onClick={() => toggleAddon('plus')}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="addon-plus"
                          checked={selectedAddons.plus}
                          onCheckedChange={() => toggleAddon('plus')}
                          data-testid="addon-plus-checkbox"
                        />
                        <div className="flex-1">
                          <label htmlFor="addon-plus" className="flex items-center gap-2 cursor-pointer">
                            <span className="font-medium text-rnb-navy">CareRevive Plus</span>
                            <span className="ml-auto font-mono-numbers font-bold text-rnb-blue">+${currentAddonPrices.plus.toLocaleString()}</span>
                          </label>
                          <p className="text-sm text-rnb-gray mt-1">
                            Implementation support for plan rollout
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Leadership Lab */}
                    <div 
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${selectedAddons.leadership_lab ? 'border-rnb-blue bg-rnb-light' : 'bg-slate-50 hover:border-slate-300'}`}
                      onClick={() => toggleAddon('leadership_lab')}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="addon-leadership"
                          checked={selectedAddons.leadership_lab}
                          onCheckedChange={() => toggleAddon('leadership_lab')}
                          data-testid="addon-leadership-checkbox"
                        />
                        <div className="flex-1">
                          <label htmlFor="addon-leadership" className="flex items-center gap-2 cursor-pointer">
                            <span className="font-medium text-rnb-navy">Leadership Lab</span>
                            <span className="ml-auto font-mono-numbers font-bold text-rnb-blue">+${currentAddonPrices.leadership_lab.toLocaleString()}</span>
                          </label>
                          <p className="text-sm text-rnb-gray mt-1">
                            Leadership training for managers
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Ongoing Partnership */}
                    <div 
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${selectedAddons.ongoing ? 'border-rnb-blue bg-rnb-light' : 'bg-slate-50 hover:border-slate-300'}`}
                      onClick={() => toggleAddon('ongoing')}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="addon-ongoing"
                          checked={selectedAddons.ongoing}
                          onCheckedChange={() => toggleAddon('ongoing')}
                          data-testid="addon-ongoing-checkbox"
                        />
                        <div className="flex-1">
                          <label htmlFor="addon-ongoing" className="flex items-center gap-2 cursor-pointer">
                            <span className="font-medium text-rnb-navy">Ongoing Partnership (First Month)</span>
                            <span className="ml-auto font-mono-numbers font-bold text-rnb-blue">+${currentAddonPrices.ongoing.toLocaleString()}</span>
                          </label>
                          <p className="text-sm text-rnb-gray mt-1">
                            Monthly support and coaching
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Staff Size Notice */}
                  {!formData.company_size && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                      <strong>Note:</strong> Add-on pricing is based on your staff size. Please select your staff size in Step 2 to see accurate pricing.
                    </div>
                  )}

                  {/* Discount Code Section */}
                  <div className="border rounded-lg p-4 mb-4 bg-slate-50">
                    <p className="text-sm font-medium text-rnb-navy mb-3">Have a discount code?</p>
                    {appliedDiscount ? (
                      <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                        <div>
                          <span className="text-green-700 font-medium">{appliedDiscount.code}</span>
                          <span className="text-green-600 text-sm ml-2">
                            ({appliedDiscount.discount_type === "percentage" 
                              ? `${appliedDiscount.discount_value}% off` 
                              : `$${appliedDiscount.discount_value} off`})
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={removeDiscount}
                          className="text-green-700 hover:text-green-900 text-sm underline"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter code"
                          value={discountCode}
                          onChange={(e) => {
                            setDiscountCode(e.target.value.toUpperCase());
                            setDiscountError("");
                          }}
                          className="flex-1"
                          data-testid="cr-discount-code-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={validateDiscountCode}
                          disabled={discountLoading || !discountCode.trim()}
                          data-testid="cr-apply-discount-btn"
                        >
                          {discountLoading ? "..." : "Apply"}
                        </Button>
                      </div>
                    )}
                    {discountError && (
                      <p className="text-red-500 text-sm mt-2">{discountError}</p>
                    )}
                  </div>

                  {/* Referral Code Section */}
                  <div className="mb-4">
                    <Label className="text-sm text-rnb-gray mb-2 block">Have a referral code? (Optional)</Label>
                    {referralValid ? (
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-purple-600" />
                          <span className="text-purple-700 font-medium">
                            Referred by {referralPartnerName}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setReferralCode("");
                            setReferralValid(null);
                            setReferralPartnerName("");
                          }}
                          className="text-purple-700 hover:text-purple-900 text-sm underline"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter referral code"
                          value={referralCode}
                          onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                          className="flex-1"
                          data-testid="cr-referral-code-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => validateReferralCode(referralCode)}
                          disabled={referralValidating || !referralCode.trim()}
                          data-testid="cr-apply-referral-btn"
                        >
                          {referralValidating ? "..." : "Apply"}
                        </Button>
                      </div>
                    )}
                    {referralValid === false && referralCode && (
                      <p className="text-red-500 text-sm mt-2">Invalid referral code</p>
                    )}
                  </div>

                  {/* Price Summary */}
                  <div className="border rounded-lg p-4 mb-6 bg-white">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-rnb-gray">CareRevive Starter Package</span>
                        <span className="font-mono-numbers text-rnb-navy">$750</span>
                      </div>
                      {selectedAddons.plus && (
                        <div className="flex justify-between text-rnb-blue">
                          <span>CareRevive Plus</span>
                          <span className="font-mono-numbers">${currentAddonPrices.plus.toLocaleString()}</span>
                        </div>
                      )}
                      {selectedAddons.leadership_lab && (
                        <div className="flex justify-between text-rnb-blue">
                          <span>Leadership Lab</span>
                          <span className="font-mono-numbers">${currentAddonPrices.leadership_lab.toLocaleString()}</span>
                        </div>
                      )}
                      {selectedAddons.ongoing && (
                        <div className="flex justify-between text-rnb-blue">
                          <span>Ongoing Partnership (First Month)</span>
                          <span className="font-mono-numbers">${currentAddonPrices.ongoing.toLocaleString()}</span>
                        </div>
                      )}
                      {appliedDiscount && (
                        <>
                          <div className="flex justify-between text-rnb-gray">
                            <span>Subtotal</span>
                            <span className="font-mono-numbers">${calculateSubtotal().toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-green-600">
                            <span>Discount ({appliedDiscount.code})</span>
                            <span className="font-mono-numbers">
                              -${(calculateSubtotal() - calculateTotal()).toLocaleString()}
                            </span>
                          </div>
                        </>
                      )}
                      <div className="border-t pt-2 flex justify-between font-bold">
                        <span className="text-rnb-navy">Total</span>
                        <span className="font-mono-numbers text-xl text-rnb-navy">${calculateTotal().toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    size="lg"
                    className="w-full btn-hover-lift"
                    onClick={handlePayment}
                    disabled={loading}
                    data-testid="carerevive-pay-btn"
                  >
                    {loading ? "Processing..." : `Pay $${calculateTotal().toLocaleString()} to Enroll`}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Progress indicator */}
                  <div className="flex items-center justify-between mb-6">
                    {[1, 2, 3, 4].map((step) => (
                      <div key={step} className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          formStep >= step ? 'bg-rnb-blue text-white' : 'bg-slate-200 text-slate-500'
                        }`}>
                          {step}
                        </div>
                        {step < 4 && (
                          <div className={`w-12 sm:w-20 h-1 mx-1 ${
                            formStep > step ? 'bg-rnb-blue' : 'bg-slate-200'
                          }`} />
                        )}
                      </div>
                    ))}
                  </div>

                  {renderFormStep()}

                  <div className="flex justify-between pt-4">
                    {formStep > 1 ? (
                      <Button type="button" variant="outline" onClick={prevStep}>
                        <ChevronLeft className="mr-2 h-4 w-4" />
                        Previous
                      </Button>
                    ) : (
                      <div />
                    )}
                    
                    {formStep < 4 ? (
                      <Button type="button" onClick={nextStep}>
                        Next
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    ) : (
                      <Button type="submit" className="btn-hover-lift" disabled={loading}>
                        {loading ? "Submitting..." : "Submit Application"}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CareRevive;
